import { AbstractFormElement, ICustomFormElement } from "./Abstract";
import { OptionsHandler } from "./Dropdown/OptionsHandler";

// Todo: selected can either be value or if not set innerText of option

type DropdownOption = HTMLOptionElement | { label: string; value: string; idx?: number; disabled?: boolean };

class Dropdown extends AbstractFormElement implements ICustomFormElement {
    declare tagTemplate: HTMLTemplateElement;

    mainEvent = "change";

    private optionsHandler: OptionsHandler;
    private filteredExternal: boolean = false;
    private tags: HTMLSpanElement;
    private tagCloseHandler: (e: PointerEvent) => void;
    private protectedNodes: HTMLElement[] = [];

    private initCompleted = false;
    protected readonly documentClickHandler: (e: PointerEvent) => void;
    private readonly optionObserver: MutationObserver;

    constructor() {
        super();
        if (!this.searchable) {
            this.clickHandler = this.handleElementClick.bind(this);
            this.documentClickHandler = this.handleDocumentClick.bind(this);
        }

        this.optionObserver = new MutationObserver(this.handleOptionMutation.bind(this));
        this.optionObserver.observe(this, {
            attributeFilter: ["disabled"],
            subtree: true,
            attributes: true,
            childList: false,
            characterData: false,
            characterDataOldValue: false
        });
    }

    /**
     * @inheritDoc
     */
    connectedCallback() {
        this.internals = this.attachInternals();
        this.form = this.internals.form;
        this.appendChild(this.template.content.cloneNode(true));
        this.initShadow()
            .initOptions()
            .initElements()
            .initAttributes()
            .attachEvents()
            .initSelectedOptions()
            .initInternals();
        this.initCompleted = true;

        // @ts-ignore
        this.internals.states[this.value !== "" ? "add" : "delete"]("--has-value");
        this.validate();
        requestAnimationFrame(() => delete this.label.dataset.noTransition);
    }

    /**
     * handle option appended to dom
     * @param node
     */
    appendChild<T extends Node>(node: T): T {
        if (node instanceof HTMLOptionElement && node.tagName === "OPTION") {
            this.optionsHandler.addItem({
                label: node.innerText,
                value: node.getAttribute("value"),
                disabled: node.disabled
            });
            if (!this.value) {
                this.selectedIndex = 0;
            }
        }
        return super.appendChild(node);
    }

    /**
     * prevent our nodes from being removed and remove item from handler
     * @param node
     */
    removeChild<T extends Node>(node: T): T {
        if (node instanceof HTMLElement && !this.protectedNodes.includes(node)) {
            if (node.tagName === "OPTION") {
                const value = this.value;
                this.optionsHandler.removeItem({ label: node.innerText, value: node.getAttribute("value") });
                this.value = value;
                if (!this.value) {
                    this.selectedIndex = 0;
                }
            }
            return super.removeChild(node);
        }
        return node;
    }

    /**
     * @inheritDoc
     */
    disconnectedCallback() {
        this.optionsHandler.destroy();
        delete this.optionsHandler;
        this.optionObserver.disconnect();
        super.disconnectedCallback();
    }

    /**
     * initialize options
     */
    initOptions() {
        this.optionsHandler = new OptionsHandler(this.querySelector("label"), this.input);
        this.optionsHandler.filteredExternal = this.filteredExternal;
        this.optionsHandler.searchable = this.searchable;
        this.optionsHandler.multiselect = this.multiselect;
        try {
            if (this.dataset.options) {
                this.options = JSON.parse(this.dataset.options);
            } else {
                const options = Array.from(this.querySelectorAll("option"));
                const ignoreOptions = this.dataset.ignoreOptions?.split(",") || [];
                this.options = options
                    .filter((o) => !ignoreOptions.includes(o.value))
                    .map((o) => {
                        return {
                            label: o.innerText.trim(),
                            value: o.value,
                            disabled: o.disabled
                        };
                    });
                if (!this.multiselect) {
                    const selected = this.querySelector("option[selected]") as HTMLOptionElement;
                    if (selected) {
                        this.value = selected.value;
                    }
                }
            }
        } catch (error) {
            /* empty */
        }
        return this;
    }

    /**
     * @inheritDoc
     */
    initElements() {
        if (this.searchable) {
            this.input.classList.add("searchable");
        }
        this.tags = this.querySelector<HTMLSpanElement>("span.tags");
        this.protectedNodes = Array.from(this.querySelectorAll(":scope > label, :scope > span"));
        super.initElements();
        if (!this.multiselect && this.value) {
            this.label.classList.add("has-value");
        }
        if (this.multiselect && this.selectedOptions.length > 0) {
            this.label.classList.add("has-value");
        }
        const icon = this.label.querySelector("span.icon");
        if (this.sort || this.time || this.dataset.icon) {
            icon.classList.remove("nav-down");
        }
        if (this.sort) {
            icon.classList.add("sort");
        }
        if (this.time) {
            icon.classList.add("time");
        }
        if (this.dataset.icon) {
            icon.classList.add(this.dataset.icon);
        }
        if (this.dataset.staticIcon !== undefined || this.time) {
            icon.classList.add("static");
        }
        if (this.multiselect) {
            this.label.querySelector<HTMLSpanElement>("span.label")?.remove();
        }
        return this;
    }

    /**
     * @inheritDoc
     */
    attachEvents() {
        this.searchable || document.addEventListener("click", this.documentClickHandler);
        this.tagCloseHandler = this.handleTagClose.bind(this);
        this.optionsHandler.onChange(this.handleChange.bind(this));
        this.optionsHandler.onBlur(() => {
            this.dispatchEvent(new Event("blur"));
        });
        this.optionsHandler.onFocus(() => {
            this.dispatchEvent(new Event("focus"));
        });
        this.optionsHandler.onOpen(() => {
            this.classList.add("open");
            this.readonly = !this.searchable;
        });
        this.optionsHandler.onClose(() => {
            this.classList.remove("open");
            this.readonly = false;
        });
        return super.attachEvents();
    }

    /**
     * detach external event listeners
     * called by abstract.disconnectedCallback
     */
    detachEvents() {
        super.detachEvents();
        document.removeEventListener("click", this.documentClickHandler);
    }

    /**
     * initialize selected options
     */
    initSelectedOptions() {
        try {
            if (this.multiselect) {
                if (this.dataset.selectedOptions) {
                    this.selectedOptions = JSON.parse(this.dataset.selectedOptions);
                } else {
                    const options = this.querySelectorAll("option[selected]") as NodeListOf<HTMLOptionElement>;
                    if (options.length) {
                        this.selectedOptions = Array.from(options).map((o) => ({
                            label: o.innerText.trim().replace("\n", " ").replace("  ", " "),
                            value: o.value
                        }));
                    }
                }
            }
        } catch (error) {
            /* empty */
        }
        return this;
    }

    /**
     * handle change events
     */
    handleChange() {
        this.initCompleted && this.input.dispatchEvent(new Event("change"));
        if (this.multiselect) {
            const tags = this.tags.querySelectorAll(".tag") as NodeListOf<HTMLSpanElement>;
            tags.forEach((t) => {
                t.removeEventListener("pointerup", this.tagCloseHandler);
            });
            this.tags.innerHTML = "";
            this.selectedOptions.forEach((s) => {
                const template = this.tagTemplate.content.cloneNode(true) as DocumentFragment;
                const tag = template.querySelector<HTMLSpanElement>(".tag");
                const text = template.querySelector<HTMLSpanElement>(".text");
                tag.dataset.idx = s.idx.toString();
                text.innerText = this.options.find((o) => o.value === s.value).label;
                template
                    .querySelector<HTMLSpanElement>(".icon.close")
                    .addEventListener("pointerup", this.tagCloseHandler, { once: true });
                this.tags.appendChild(template);
            });
        }
        if (this.multiselect) {
            this.label.classList.toggle("has-value", this.selectedOptions.length > 0);
        } else {
            this.label.classList.toggle("has-value", !!this.input.value);
        }
        // @ts-ignore
        this.internals.states[this.value !== "" ? "add" : "delete"]("--has-value");
    }

    /**
     * handle tag close events
     * @param {PointerEvent} e
     */
    handleTagClose(e: PointerEvent) {
        const target = e.target as HTMLSpanElement;
        const tag = target.closest(".tag") as HTMLSpanElement;
        this.optionsHandler.deselectOption(parseInt(tag.dataset.idx));
        tag.remove();
    }

    /**
     * if not searchable we cannot focus since on ios the keyboard pops up
     * @param {PointerEvent} e
     */
    handleElementClick(e: PointerEvent): void {
        e.preventDefault();
        if (!this.disabled && !this.readonly) {
            this.input.dispatchEvent(new FocusEvent("focus"));
        }
    }

    /**
     * handle option mutations
     * @param {MutationRecord[]} mutationList
     */
    handleOptionMutation(mutationList: MutationRecord[]): void {
        mutationList.forEach((mutation) => {
            const target = mutation.target as HTMLElement;
            if (target.parentNode === this) {
                if (mutation.type === "attributes" && mutation.attributeName == "disabled") {
                    this.optionsHandler.disableOption(
                        (target as HTMLOptionElement).value,
                        (target as HTMLOptionElement).disabled
                    );
                }
            }
        });
    }

    /**
     * if not searchable we need to use document click to close dropdown
     * @param {PointerEvent} e
     */
    handleDocumentClick(e: PointerEvent): void {
        const target = e.target as HTMLElement;
        if (!this.searchable && target.classList.contains("combobox-item") && target.dataset.disabled) return;
        if (!this.optionsHandler.list.hidden && e.composedPath().indexOf(this) < 0) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.input.dispatchEvent(new FocusEvent("blur"));
        }
    }

    get firstChild() {
        return this.querySelector("option");
    }

    get options(): DropdownOption[] {
        const options = this.querySelectorAll("option");
        if (options.length) {
            return Array.from(options);
        }
        return this.optionsHandler.items;
    }

    set options(v: { label: string; value: string; idx?: number; disabled?: boolean }[]) {
        this.optionsHandler.items = v;
    }

    get value() {
        return this.optionsHandler?.value?.value;
    }

    set value(v) {
        this.optionsHandler.value = this.options.find((o) => o.value === v || o.label === v);
        this.validate();
    }

    get selectedIndex() {
        return this.optionsHandler?.selectedIndex > -1 ? this.optionsHandler.selectedIndex : 0;
    }

    set selectedIndex(v) {
        this.optionsHandler.selectedIndex = v;
        this.label.classList.toggle("has-value", !!this.input.value);
        this.validate();
    }

    get sort() {
        return this.dataset.type === "sort";
    }

    get time() {
        return this.dataset.type === "time";
    }

    get searchable() {
        return this.dataset.type === "searchable";
    }

    get multiselect() {
        return this.dataset.type === "multiselect";
    }

    get selectedOptions() {
        return this.optionsHandler?.selectedOptions || [];
    }

    set selectedOptions(options: { label: string; value: string; idx?: number; disabled?: boolean }[]) {
        this.optionsHandler.selectedOptions = options;
        this.label.classList.toggle("has-value", this.selectedOptions.length > 0);
        this.validate();
    }
}

Dropdown.prototype.tagTemplate = document.createElement("template");
Dropdown.prototype.tagTemplate.innerHTML = /*html*/ `
<span class="tag">
    <span class="icon close"></span>
    <span class="text"></span>
</span>
`;
Dropdown.prototype.shadowStyle = document.createElement("template");
Dropdown.prototype.shadowStyle.innerHTML = `
${AbstractFormElement.prototype.shadowStyle.innerHTML}
<style>
span.input {
    position: relative;
    display: inline-block;
}

input:not(.searchable) {
    z-index: -1;
    position: relative;
}
</style>
`;

Dropdown.prototype.template = document.createElement("template");

Dropdown.prototype.template.innerHTML = /*html*/ `
<label class="std-fe" data-no-transition>
    <span class="label"></span>
    <span class="input"></span>
    <span class="icon nav-down"></span>
</label>
<span class="tags"></span>
<span class="error"></span>
<span class="help"></span>
`;

customElements.define("x-dropdown", Dropdown);

export { Dropdown };
